import _ from 'lodash'
import { RESOURCES, RIGHTS } from '../constants/permissions'

class UserPermissions {
  constructor(userAuthorities, resourceAuthorityMap) {
    this.userAuthorities = userAuthorities
    this.resourceAuthorityMap = resourceAuthorityMap
    this.userResourcePermissions = this.getAllUserResourcePermissions()
  }

  /**
   * getUserResourcePermissions - For each resource return the max rights the user has to that resource
   *
   * @param {Array of Strings} resources        List of resources that you would like information on
   * @param {Array of Strings} userAuthorities  List of the user's authority's
   * @param {Object} resourceAuthorityMap       System (config) defined mapping or resource to authority required
   *
   * @returns {Object} Mapping of resource to the user's max rights for that resource
   */
  getUserResourcePermissions = resources => {
    const userAuthoritySet = new Set(this.userAuthorities)
    return _.reduce(
      resources,
      (permissions, resource) => {
        // get authority's required to access this resource
        if (resource) {
          let right = RIGHTS.NONE
          const authorities = this.resourceAuthorityMap[resource]

          if (_.isArray(authorities)) {
            // For LISTING_ATTRIBUTES_PAGE, 2 set of roles can access: [MLC, MLCM] + ["", ASCM]
            if (resource === RESOURCES.LISTING_ATTRIBUTES_PAGE) {
              const [
                mktReadAuthority,
                mktWriteAuthority,
                asyncReadAuthority,
                asyncWriteAuthority,
              ] = authorities
              if (
                userAuthoritySet.has(mktWriteAuthority) ||
                userAuthoritySet.has(asyncWriteAuthority)
              ) {
                right = RIGHTS.WRITE
              } else if (
                userAuthoritySet.has(mktReadAuthority) ||
                userAuthoritySet.has(asyncReadAuthority)
              ) {
                right = RIGHTS.READ
              }
            } else {
              // see if user has a match on authority
              const [readAuthority, writeAuthority] = authorities
              if (userAuthoritySet.has(writeAuthority)) {
                right = RIGHTS.WRITE
              } else if (userAuthoritySet.has(readAuthority)) {
                right = RIGHTS.READ
              }
            }
          }
          permissions[resource] = right
        }

        return permissions
      },
      {}
    )
  }

  /**
   * getAllUserResourcePermissions - gets a user's permissions for all the resources
   *
   * @returns {Object} Mapping of resource to the user's max rights for that resource
   */
  getAllUserResourcePermissions = () =>
    this.getUserResourcePermissions(_.values(RESOURCES))

  /**
   * hasAccess - return true or fals if user has access to that resource at that level.
   * If user has WRITE access then it is assumed he has READ access as well.
   *
   * @param {String} resource    Resource in question
   * @param {String} accessLevel Level of access in question
   *
   * @returns {Boolean} If user has that level of access
   */
  hasAccess = (resource, accessLevel) => {
    const permission = this.userResourcePermissions[resource]
    if (!permission) return false

    switch (accessLevel) {
      case RIGHTS.NONE:
        return true
      case RIGHTS.WRITE:
        return permission === RIGHTS.WRITE
      case RIGHTS.READ:
        return permission === RIGHTS.READ || permission === RIGHTS.WRITE
      default:
        throw new Error('Invalid accessLevel queried')
    }
  }
}

export default UserPermissions

/**
 * General utility to get the authority corresponding to a particular resource and right
 */
export const getResourceAuthority = (
  resourceAuthorityMap,
  resourceName,
  right = RIGHTS.READ
) => {
  const authorityTuple = resourceAuthorityMap[resourceName]
  if (
    _.isEmpty(authorityTuple) ||
    !_.isArray(authorityTuple)
    // This is because we will allow [MLC, MLCM] + ["" + ASCM] to visit LISTING_ATTRIBUTES_PAGE
    // That way the authorityTuple is lenth(4)
    // authorityTuple.length !== 2
  ) {
    throw new Error('Invalid resource name specified')
  }

  if (right !== RIGHTS.WRITE && right !== RIGHTS.READ) {
    throw new Error('Invalid right specified')
  }

  const authority = authorityTuple[right === RIGHTS.WRITE ? 1 : 0]
  if (_.isEmpty(authority)) {
    throw new Error('Unavailable authority')
  }

  return authority
}
